import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { toast as notify } from "react-toastify";
import {
    Button, Table, Form, Row, Col, Badge, Spinner, Modal, Card,
    Tabs, Tab, Toast, ToastContainer, OverlayTrigger, Tooltip, InputGroup,
} from "react-bootstrap";
import {
    addFormData,
    fbBackfillFormAPI, fbConnectedAPI, fbConnectUrl, fbHealthAPI,
    fbListTenantsAPI, fbPullInsightsAPI, fbRefreshFormsAPI, fbSubscribePageAPI
} from "../../utils/api";
import { FaRegHandPointRight } from "react-icons/fa";
import withAuth from "../../utils/withAuth";

/* ---------- Helpers ---------- */
const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
const safeArray = (x) => (Array.isArray(x) ? x : []);
const values = (v) => (Array.isArray(v) ? v : isObj(v) ? Object.values(v) : []);

const sanitizeConnected = (raw) => {
    const c = isObj(raw) ? raw : {};
    const forms = values(c.forms)
        .filter(Boolean)
        .map((f) => (isObj(f) ? f : {}))
        .map((f) => ({
            id: f.id ?? null,
            form_id: f.form_id ?? null,
            form_name: typeof f.form_name === "string" ? f.form_name : String(f.form_name ?? ""),
            status: typeof f.status === "string" ? f.status : String(f.status ?? ""),
            page_row_id: f.page_row_id ?? null,
        }));

    return {
        pages: values(c.pages).filter(isObj),
        forms,
        adAccounts: values(c.adAccounts).filter(isObj),
        lastSyncAt: c.lastSyncAt || null,
    };
};

/* ---------- Component ---------- */

const FacebookAuth = () => {
    const [tenants, setTenants] = useState([]);
    const [tenantId, setTenantId] = useState("");
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState({ pages: [], forms: [], adAccounts: [], lastSyncAt: null });
    const [health, setHealth] = useState({});
    const [validated, setValidated] = useState(false);
    const ppcid = localStorage.getItem("ppcdata");

    const [showBackfill, setShowBackfill] = useState(false);
    const [bfFormId, setBfFormId] = useState("");
    const [bfSince, setBfSince] = useState("");
    const [bfUntil, setBfUntil] = useState("");

    const [showInsights, setShowInsights] = useState(false);
    const [insAccount, setInsAccount] = useState("");
    const [insSince, setInsSince] = useState("");
    const [insUntil, setInsUntil] = useState("");
    const [insLevel, setInsLevel] = useState("campaign");

    const [busyAction, setBusyAction] = useState(false);
    const [toast, setToast] = useState(null);
    const [formQuery, setFormQuery] = useState("");

    /* strip Facebook #_=_ once */
    useEffect(() => {
        if (window.location.hash === "#_=_") {
            const clean = window.location.pathname + window.location.search;
            window.history.replaceState(null, document.title, clean);
        }
    }, []);

    /* read ?tenant=… on first load */
    useEffect(() => {
        const usp = new URLSearchParams(window.location.search);
        const t = usp.get("tenant");
        if (t) setTenantId(String(t));
    }, []);

    /* loaders */
    const loadTenants = async () => {
        try {
            const res = await fbListTenantsAPI(ppcid);
            const list = res?.data || [];
            setTenants(list);
            if (list?.length && !tenantId) setTenantId(String(list[0].id));
        } catch (e) {
            setToast({ type: "danger", msg: `Load tenants failed: ${e?.message || "Error"}` });
        }
    };

    const loadConnected = async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const [connRes, healthRes] = await Promise.all([fbConnectedAPI(id), fbHealthAPI(id)]);
            setConnected(sanitizeConnected(connRes?.data));
            setHealth(healthRes?.data || {});
        } catch (e) {
            setToast({ type: "danger", msg: `Load data failed: ${e?.message || "Error"}` });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTenants(); }, []);
    useEffect(() => { if (tenantId) loadConnected(tenantId); }, [tenantId]);

    /* actions */
    const handleConnect = () => {
        if (!tenantId) return setToast({ type: "danger", msg: "Select a tenant first." });
        window.location.href = fbConnectUrl(tenantId);
    };

    const handleRefresh = async () => {
        if (!tenantId) return;
        setBusyAction(true);
        try {
            await fbRefreshFormsAPI(tenantId);
            await loadConnected(tenantId);
            setToast({ type: "success", msg: "Refreshed pages & forms." });
        } catch (e) {
            setToast({ type: "danger", msg: `Refresh failed: ${e?.message || "Error"}` });
        } finally {
            setBusyAction(false);
        }
    };

    const handleSubscribe = async (pageId) => {
        setBusyAction(true);
        try {
            await fbSubscribePageAPI(pageId);
            await loadConnected(tenantId);
            setToast({ type: "success", msg: `Subscribed page ${pageId}.` });
        } catch (e) {
            setToast({ type: "danger", msg: `Subscribe failed: ${e?.message || "Error"}` });
        } finally {
            setBusyAction(false);
        }
    };

    const openBackfill = (formId = "") => {
        setBfFormId(formId);
        setBfSince("");
        setBfUntil("");
        setShowBackfill(true);
    };

    const submitBackfill = async () => {
        if (!bfFormId || !bfSince || !bfUntil)
            return setToast({ type: "danger", msg: "Form, Since and Until are required." });
        setBusyAction(true);
        try {
            const r = await fbBackfillFormAPI({ form_id: bfFormId, since: bfSince, until: bfUntil });
            setShowBackfill(false);
            setToast({ type: "success", msg: `Backfill synced: ${r?.data?.synced ?? 0}` });
            await loadConnected(tenantId);
        } catch (e) {
            setToast({ type: "danger", msg: `Backfill failed: ${e?.message || "Error"}` });
        } finally {
            setBusyAction(false);
        }
    };

    const openInsights = (adAccountId = "") => {
        setInsAccount(adAccountId);
        setInsSince("");
        setInsUntil("");
        setInsLevel("campaign");
        setShowInsights(true);
    };

    const submitInsights = async () => {
        if (!insAccount || !insSince || !insUntil || !insLevel)
            return setToast({ type: "danger", msg: "Account, Since, Until and Level are required." });
        setBusyAction(true);
        try {
            const r = await fbPullInsightsAPI({ ad_account_id: insAccount, since: insSince, until: insUntil, level: insLevel });
            setShowInsights(false);
            setToast({ type: "success", msg: `Insights saved: ${r?.data?.rows_saved ?? 0}` });
        } catch (e) {
            setToast({ type: "danger", msg: `Insights pull failed: ${e?.message || "Error"}` });
        } finally {
            setBusyAction(false);
        }
    };

    /* memo: group forms by page_row_id, with optional search */
    const formsByPageRow = useMemo(() => {
        const map = {};
        for (const raw of safeArray(connected.forms)) {
            const f = isObj(raw) ? raw : {};
            const key = f.page_row_id;
            if (!key) continue;
            if (formQuery) {
                const text = `${f.form_name || ""} ${f.form_id || ""}`.toLowerCase();
                if (!text.includes(formQuery.toLowerCase())) continue;
            }
            (map[key] = map[key] || []).push(f);
        }
        return map;
    }, [connected.forms, formQuery]);

    const pagesCount = safeArray(connected.pages).length;
    const formsCount = safeArray(connected.forms).length;
    const adAccountsCount = safeArray(connected.adAccounts).length;

    const Stat = ({ title, value, sub }) => (
        <div className="stat-card">
            <div className="stat-title">{title}</div>
            <div className="stat-value">{value}</div>
            {sub ? <div className="stat-sub">{sub}</div> : null}
        </div>
    );

    const EmptyState = ({ title, note, action }) => (
        <div className="empty-state">
            <div className="emoji">📂</div>
            <h6>{title}</h6>
            <p className="text-muted">{note}</p>
            {action}
        </div>
    );

    const [values, setInputValue] = useState();
    const [tenantshow, setTenantShow] = useState(false);

    const handleTenantClose = () => setTenantShow(false);

    const habdleTenant = () => {
        setTenantShow(true);
    }
    const handleInputs = e => {
        setInputValue({
            ...values,
            [e.target.name]: e.target.value,
        });
    };

    const handleTenantSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const payload = {
                name: values.tenant_name?.trim(),
                ppc_id: ppcid,
            };
            const res = await addFormData("ppc-add-facebook-tenant-name", payload);
            const status = res?.data?.status;

            if (status === 1){
                loadTenants();
                notify.success("Tenant has been added successfully!!");
            } 
            else if (status === 2) notify.warning("Tenant already exists!!");
            else notify.error("Something went wrong!!");

            setTenantShow(false);
        } catch {
            notify.error("Something went wrong!!");
        }
        setValidated(true);
    };

    return (
        <div className="misDashboard fb-surface">
            <Helmet><title>SATKARTAR :: Facebook Integration</title></Helmet>
            <ContentWrapper>
                <Modal show={tenantshow} onHide={handleTenantClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>ADD TENANT</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form noValidate validated={validated} onSubmit={handleTenantSubmit} method="post">
                            <Form.Group as={Col} md="12" controlId="validationCustom01">
                                <Form.Label>Enter Tenant Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Tenant Name"
                                    name="tenant_name"
                                    onChange={handleInputs}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    <FaRegHandPointRight /> Please provide a valid Tenant Name
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Button type="submit" className="btn btn-sm" style={{ display: "flex", margin: "auto", marginTop: "10px" }}>Submit</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
                {/* Hero */}
                <div className="fb-hero">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h2 className="fb-hero-title">Facebook Integration</h2>
                            <p className="fb-hero-sub">Connect Pages, capture Leads in real time, and pull Ad Spend & Insights.</p>
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                            <OverlayTrigger overlay={<Tooltip>Connect Facebook for the selected tenant</Tooltip>}>
                                <span>
                                    <Button variant="primary" className="fb-btn me-2" onClick={handleConnect} disabled={!tenantId}>
                                        CONNECT FACEBOOK
                                    </Button>
                                </span>
                            </OverlayTrigger>
                            <Button variant="outline-primary" className="fb-rebtn" onClick={handleRefresh} disabled={!tenantId || busyAction}>
                                {busyAction ? "REFRESHING…" : "REFRESH"}
                            </Button>
                            <Button variant="secondary" className="fb-btn" onClick={habdleTenant}>
                                ADD TENANT
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Row className="g-4">
                    {/* Control Panel */}
                    <Col lg={4}>
                        <Card className="fb-card p-3">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Control Panel</h6>
                                    {loading && <Spinner animation="border" size="sm" />}
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Tenant</Form.Label>
                                    <Form.Select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="fb-input">
                                        {tenants.map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Row className="g-3 mb-3">
                                    <Col xs={12} className="d-flex align-items-center gap-2">
                                        <Badge bg={health?.oauth_ok ? "success" : "danger"}>OAuth {health?.oauth_ok ? "OK" : "Check"}</Badge>
                                        <Badge bg={health?.webhook_ok ? "success" : "warning"}>Webhook {health?.webhook_ok ? "OK" : "Check"}</Badge>
                                    </Col>
                                    <Col xs={12}>
                                        <small className="text-muted">
                                            {connected?.lastSyncAt ? `Last sync: ${connected.lastSyncAt}` : "No sync yet"}
                                        </small>
                                    </Col>
                                </Row>

                                <Row className="g-3">
                                    <Col sm={4}><Stat title="Pages" value={pagesCount} /></Col>
                                    <Col sm={4}><Stat title="Forms" value={formsCount} /></Col>
                                    <Col sm={4}><Stat title="Ad Accounts" value={adAccountsCount} /></Col>
                                </Row>

                                <div className="d-grid gap-2 mt-4">
                                    <Button variant="primary" className="fb-btn" onClick={handleConnect} disabled={!tenantId}>
                                        CONNECT / RECONNECT
                                    </Button>
                                    <Button variant="outline-secondary" className="fb-btn" onClick={handleRefresh} disabled={!tenantId || busyAction}>
                                        {busyAction ? "REFRESHING…" : "REFRESH PAGES & FORMS"}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Main content */}
                    <Col lg={8}>
                        <Card className="fb-card">
                            <Card.Body>
                                <Tabs defaultActiveKey="pages" id="fb-tabs" className="fb-tabs" justify>
                                    <Tab eventKey="pages" title="Pages & Forms">
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <InputGroup>
                                                    <InputGroup.Text>🔎</InputGroup.Text>
                                                    <Form.Control
                                                        placeholder="Search forms by name or ID…"
                                                        value={formQuery}
                                                        onChange={(e) => setFormQuery(e.target.value)}
                                                        className="fb-input"
                                                    />
                                                </InputGroup>
                                            </Col>
                                        </Row>

                                        <div className="table-responsive">
                                            <Table hover className="align-middle fb-table">
                                                <thead>
                                                    <tr>
                                                        <th>Page</th>
                                                        <th>Status</th>
                                                        <th>Lead Forms</th>
                                                        <th className="text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeArray(connected.pages).map((p) => {
                                                        const pageId = p?.id ?? p?.page_id;
                                                        const pageForms = safeArray(formsByPageRow[p?.id]);

                                                        return (
                                                            <tr key={pageId || Math.random()}>
                                                                <td>
                                                                    <div className="fw-semibold text-white">{p?.page_name || p?.page_id || "Unnamed page"}</div>
                                                                    <div className="text-white small">{p?.page_id || ""}</div>
                                                                </td>

                                                                <td>
                                                                    <Badge bg={p?.is_active ? "success" : "secondary"} className="me-2">
                                                                        {p?.is_active ? "Active" : "Disabled"}
                                                                    </Badge>
                                                                    <Badge bg={p?.is_subscribed_webhook ? "primary" : "warning"}>
                                                                        {p?.is_subscribed_webhook ? "Webhook On" : "Webhook Off"}
                                                                    </Badge>
                                                                </td>

                                                                <td>
                                                                    {pageForms.length === 0 ? (
                                                                        <span className="text-muted small">No forms</span>
                                                                    ) : (
                                                                        <div className="small">
                                                                            {pageForms.map((item, idx) => {
                                                                                const f = isObj(item) ? item : {};
                                                                                const statusText = typeof f.status === "string" ? f.status : String(f.status ?? "");
                                                                                const isActive = statusText.toLowerCase() === "active";
                                                                                return (
                                                                                    <div key={f.id || f.form_id || `${pageId}-f${idx}`} className="mb-1">
                                                                                        <span className="fw-semibold">{f.form_name || f.form_id || "Unnamed form"}</span>{" "}
                                                                                        <Badge bg={isActive ? "success" : "secondary"}>{statusText || "—"}</Badge>
                                                                                        <Button
                                                                                            size="sm"
                                                                                            variant="outline-success"
                                                                                            className="ms-2 py-0 px-2"
                                                                                            onClick={() => openBackfill(f.form_id || "")}
                                                                                        >
                                                                                            Backfill
                                                                                        </Button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                <td className="text-end">
                                                                    <button
                                                                        size="sm"
                                                                        disabled={busyAction}
                                                                        onClick={() => handleSubscribe(p?.page_id)}
                                                                        className="px-3 btn-sub"
                                                                    >
                                                                        {busyAction ? "Working…" : "Subscribe Webhook"}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Tab>

                                    <Tab eventKey="ads" title="Ad Accounts">
                                        {safeArray(connected.adAccounts).length === 0 ? (
                                            <EmptyState
                                                title="No ad accounts found"
                                                note="Connect Facebook with ads_read permission to view and pull insights."
                                                action={<Button onClick={handleConnect} disabled={!tenantId} className="fb-btn">CONNECT FACEBOOK</Button>}
                                            />
                                        ) : (
                                            <div className="table-responsive">
                                                <Table hover className="align-middle fb-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Account</th>
                                                            <th>Currency</th>
                                                            <th>Timezone</th>
                                                            <th className="text-end">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {safeArray(connected.adAccounts).map((a) => (
                                                            <tr key={a?.ad_account_id}>
                                                                <td>
                                                                    <div className="fw-semibold text-white">{a?.account_name || a?.ad_account_id}</div>
                                                                    <div className="text-white small">act_{a?.ad_account_id}</div>
                                                                </td>
                                                                <td className="text-white small">{a?.currency || "-"}</td>
                                                                <td className="text-white small">{a?.timezone_name || "-"}</td>
                                                                <td className="text-end">
                                                                    <button onClick={() => openInsights(a?.ad_account_id)} className="px-3 text-white btninsite">
                                                                        Pull Insights
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <ToastContainer position="bottom-end" className="p-3">
                    {!!toast && (
                        <Toast onClose={() => setToast(null)} bg={toast.type === "danger" ? "danger" : "success"} show delay={4000} autohide>
                            <Toast.Body className="text-white">{toast.msg}</Toast.Body>
                        </Toast>
                    )}
                </ToastContainer>

                {/* Backfill modal */}
                <Modal show={showBackfill} onHide={() => setShowBackfill(false)} centered>
                    <Modal.Header closeButton><Modal.Title>Backfill Leads</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Form ID</Form.Label>
                                <Form.Control
                                    placeholder="e.g., 123456789012345"
                                    value={bfFormId}
                                    onChange={(e) => setBfFormId(e.target.value)}
                                    className="fb-input"
                                />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Since</Form.Label>
                                        <Form.Control type="date" value={bfSince} onChange={(e) => setBfSince(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Until</Form.Label>
                                        <Form.Control type="date" value={bfUntil} onChange={(e) => setBfUntil(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant="secondary" onClick={() => setShowBackfill(false)}>Close</Button>
                        <Button size="sm" variant="primary" disabled={busyAction} onClick={submitBackfill}>
                            {busyAction ? "Working…" : "Start Backfill"}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Insights modal */}
                <Modal show={showInsights} onHide={() => setShowInsights(false)} centered>
                    <Modal.Header closeButton><Modal.Title>Pull Ad Insights (Spend)</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Ad Account ID</Form.Label>
                                <Form.Control
                                    placeholder="numeric id, e.g. 1234567890"
                                    value={insAccount}
                                    onChange={(e) => setInsAccount(e.target.value)}
                                    className="fb-input"
                                />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Since</Form.Label>
                                        <Form.Control type="date" value={insSince} onChange={(e) => setInsSince(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Until</Form.Label>
                                        <Form.Control type="date" value={insUntil} onChange={(e) => setInsUntil(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group>
                                <Form.Label>Level</Form.Label>
                                <Form.Select value={insLevel} onChange={(e) => setInsLevel(e.target.value)} className="fb-input">
                                    <option value="campaign">Campaign</option>
                                    <option value="adset">Ad Set</option>
                                    <option value="ad">Ad</option>
                                </Form.Select>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant="secondary" onClick={() => setShowInsights(false)}>Close</Button>
                        <Button size="sm" variant="primary" disabled={busyAction} onClick={submitInsights}>
                            {busyAction ? "Working…" : "Pull Insights"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(FacebookAuth);
